'use client';

import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Package, FileText, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormSwitch } from '@/components/forms/form-switch';
import { FormArrayCombobox } from '@/components/forms/form-array-combobox';
import { FormMultiCombobox } from '@/components/forms/form-multi-combobox';
import { labQueries } from '@/features/labs';
import {
  useDynamicFormLabMode,
  type OfficeBiLabel
} from '@/features/configurations';
import { createBasicInfoSchema, type BasicInfoFormValues } from '../schemas';
import type { DynamicFormType } from '../types';

interface StepBasicInfoProps {
  defaultValues?: Partial<BasicInfoFormValues>;
  onNext: (values: BasicInfoFormValues) => void;
}

const FORM_TYPES: {
  value: DynamicFormType;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { value: 'registration', labelKey: 'typeRegistration', icon: ClipboardList },
  { value: 'filling', labelKey: 'typeFilling', icon: Package }
];

export function StepBasicInfo({ defaultValues, onNext }: StepBasicInfoProps) {
  const t = useTranslations('dynamicForms');
  const tv = useTranslations('validation');
  const locale = useLocale();
  const labMode = useDynamicFormLabMode();

  const schema = useMemo(
    () => createBasicInfoSchema(tv, { labMode }),
    [tv, labMode]
  );

  const form = useForm<BasicInfoFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: defaultValues?.name ?? '',
      type: defaultValues?.type ?? undefined,
      is_default: defaultValues?.is_default ?? false,
      is_double_sided: defaultValues?.is_double_sided ?? false,
      offices: defaultValues?.offices ?? []
    }
  });

  const selectedType = useWatch({ control: form.control, name: 'type' });

  const { data: labsData } = useQuery(labQueries.all());
  const officeOptions = useMemo(() => {
    const settings =
      (
        labsData as
          | { data?: { offices?: OfficeBiLabel[] }; offices?: OfficeBiLabel[] }
          | undefined
      )?.data ?? labsData;
    const raw = (settings as { offices?: OfficeBiLabel[] } | undefined)
      ?.offices;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((o) => o.status === 'active')
      .map((o) => ({
        value: o.ar,
        label: locale === 'ar' ? o.ar : (o.en ?? o.ar)
      }));
  }, [labsData, locale]);

  const showOfficesField = selectedType === 'registration';

  // Double-sided printing puts a barcode on each *container page*, so it means
  // nothing for a packing form — hide it and keep the stored value false.
  const showDoubleSidedField = selectedType !== 'filling';

  const handleSubmit = form.handleSubmit((values) => {
    onNext({
      ...values,
      // The switch is hidden for a packing form; a value left over from a type
      // switch would otherwise be saved without the operator ever seeing it.
      is_double_sided:
        values.type === 'filling' ? false : values.is_double_sided,
      offices: (values.offices ?? []).filter(
        (o): o is string => typeof o === 'string' && o.length > 0
      )
    });
  });

  return (
    <Form form={form} onSubmit={handleSubmit}>
      <div className='flex flex-col items-center'>
        <div className='bg-primary/10 mb-4 flex h-16 w-16 items-center justify-center rounded-full'>
          <FileText className='text-primary h-8 w-8' />
        </div>
        <h3 className='mb-2 text-lg font-semibold'>{t('whatIsFormName')}</h3>
        <p className='text-muted-foreground mb-6 text-sm'>
          {t('formNameHint')}
        </p>
      </div>

      <div className='mx-auto max-w-md space-y-6'>
        <FormInput
          control={form.control}
          name='name'
          label={t('formName')}
          placeholder={t('formNamePlaceholder')}
          required
        />

        <div>
          <p className='mb-3 text-sm font-medium'>
            {t('formType')}
            <span className='ms-1 text-red-500'>*</span>
          </p>
          <div className='grid grid-cols-2 gap-3'>
            {FORM_TYPES.map((ft) => {
              const Icon = ft.icon;
              const isSelected = selectedType === ft.value;
              return (
                <button
                  key={ft.value}
                  type='button'
                  onClick={() => {
                    form.setValue('type', ft.value, { shouldValidate: true });
                  }}
                  className={cn(
                    'flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5 ring-primary ring-2'
                      : 'border-border hover:border-primary/30'
                  )}
                >
                  <Icon
                    className={cn(
                      'h-6 w-6',
                      isSelected ? 'text-primary' : 'text-muted-foreground'
                    )}
                  />
                  <span
                    className={cn(
                      'text-sm font-medium',
                      isSelected ? 'text-primary' : 'text-foreground'
                    )}
                  >
                    {t(ft.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>
          {form.formState.errors.type && (
            <p className='mt-2 text-sm text-red-500'>
              {form.formState.errors.type.message}
            </p>
          )}
        </div>

        {showOfficesField &&
          (labMode === 'single' ? (
            <FormArrayCombobox
              control={form.control}
              name='offices'
              label={t('officesLabelSingle')}
              placeholder={t('officesPlaceholder')}
              description={t('officesHintSingle')}
              options={officeOptions}
              required
            />
          ) : (
            <FormMultiCombobox
              control={form.control}
              name='offices'
              label={t('officesLabelMulti')}
              placeholder={t('officesPlaceholder')}
              description={t('officesHintMulti')}
              options={officeOptions}
              required
            />
          ))}

        <FormSwitch
          control={form.control}
          name='is_default'
          label={t('setAsDefault')}
          description={t('setAsDefaultHint')}
        />

        {showDoubleSidedField && (
          <FormSwitch
            control={form.control}
            name='is_double_sided'
            label={t('doubleSided')}
            description={t('doubleSidedHint')}
          />
        )}
      </div>

      <div className='mt-8 flex items-center justify-between'>
        <div />
        <span className='text-muted-foreground text-sm'>
          {t('stepOf', { current: 1, total: 3 })}
        </span>
        <Button type='submit'>
          {t('next')}
          <ArrowRight className='ms-2 h-4 w-4 rtl:rotate-180' />
        </Button>
      </div>
    </Form>
  );
}
